import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * The box every table that can be wider than the screen sits in: the table scrolls sideways inside
 * it, never the page (DESIGN.md rule 8). It is positioned (`relative`) so that absolutely positioned
 * content inside the table, such as a hidden "Actions" header (`sr-only`), is clipped by this box
 * instead of widening the page on a phone. `both` also scrolls up and down (a box with a max height).
 *
 * A box that scrolls must be reachable from the keyboard (WCAG 2.1.1; axe `scrollable-region-focusable`),
 * so it is a focusable region with a name: `label` is required and says in plain words what the table
 * is ("Revenue by month table"). It shows an inset focus ring when it has the keyboard focus (inset, so a parent that clips can't hide it).
 */
export const TableScroll = forwardRef<HTMLDivElement, Omit<HTMLAttributes<HTMLDivElement>, "aria-label"> & { label: string; both?: boolean }>(function TableScroll({ className, both, label, ...rest }, ref) {
  return (
    <div
      tabIndex={0}
      role="region"
      {...rest}
      ref={ref}
      aria-label={label}
      className={cn(
        "relative rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        both ? "overflow-auto" : "overflow-x-auto",
        className,
      )}
    />
  );
});
